import { useMemo } from "react";

import { bsMath, calculatePercentChange } from "@bridgesplit/utils";
import { StrategyDuration, TokenListMetadata } from "@bridgesplit/abf-sdk";

import {
    AbfLoanExpanded,
    AbfOrderFundingType,
    Ledger,
    LedgerDebt,
    LedgerExpanded,
    LoanEventActionType,
    DerivedLoanEventActionType,
    DerivedLoanEventActionMetadata,
    ParsedLoanEventActionMetadata,
    ParsedLoanEventExpanded,
    TokenStandard
} from "../types";
import { summarizeLedgerAccount } from "./order";
import {
    getZcCollateral,
    getZcLedger,
    getZcLoanCollateral,
    getZcLoanLiquidationThreshold,
    useLenderStrategies
} from "../api";

export function calculateCurrentLtv({
    totalOwedValue,
    loanCollateralValue
}: {
    totalOwedValue: number | undefined;
    loanCollateralValue: number | undefined;
}): number {
    return bsMath.div(totalOwedValue, loanCollateralValue);
}

export function calculateHealthRatio(
    totalOwedValue: number | undefined,
    loanCollateralValue: number | undefined,
    liquidationThreshold: number | undefined
): number {
    if (totalOwedValue === undefined || loanCollateralValue === undefined || liquidationThreshold === undefined)
        return 0;

    if (totalOwedValue === 0) return 1;
    const loanRatio = calculateCurrentLtv({ totalOwedValue, loanCollateralValue });

    return calculateHealthRatioFromLtv(loanRatio, liquidationThreshold);
}

export function calculateHealthRatioFromLtv(ltv: number | undefined, liquidationThreshold: number | undefined): number {
    if (ltv === undefined || liquidationThreshold === undefined) return 0;

    if (ltv === 0) return 1;
    const health = ltv * (1 / liquidationThreshold);
    const healthRatio = Math.max(1 - health, 0);
    return healthRatio;
}

export function calculateLiquidationPrice(
    totalPrincipalAmount: number | undefined,
    loanCollateralAmount: number | undefined,
    liquidationThreshold: number | undefined
): number {
    const collateralizationRatio = bsMath.div(loanCollateralAmount, totalPrincipalAmount);
    const liquidationPrice = bsMath.mul(liquidationThreshold, collateralizationRatio) ?? 0;
    return 1 / liquidationPrice;
}

export function isLoanZeroCoupon(loanExpanded: AbfLoanExpanded | undefined): boolean {
    return loanExpanded?.ledger.length === 1;
}

export function isSimpleLoan(loanExpanded: AbfLoanExpanded | undefined): boolean {
    if (!loanExpanded) return false;
    return isLoanZeroCoupon(loanExpanded);
}

export function isLoopLoan(loanExpanded: AbfLoanExpanded | undefined) {
    return loanExpanded?.loanType === AbfOrderFundingType.Loop;
}

export function getNextPaymentDate(loan: AbfLoanExpanded | undefined): number | undefined {
    if (!loan) {
        return undefined;
    }
    const firstNotFilledElement = loan.ledger.find((el) => !el.isFilled);
    if (!firstNotFilledElement) return undefined;
    return firstNotFilledElement.endTime;
}

export function calculateLoanLiquidationPrice(
    loanExpanded: AbfLoanExpanded | undefined,
    ledgerAccount?: LedgerExpanded
) {
    const firstLedger = getZcLedger(loanExpanded);
    const selectedLedgerAccount = ledgerAccount ?? firstLedger;
    if (!loanExpanded || loanExpanded.collateral.length !== 1 || !selectedLedgerAccount) return undefined;

    const collateral = getZcCollateral(loanExpanded);
    if (!collateral) return undefined;
    const totalOutstanding = summarizeLedgerAccount(selectedLedgerAccount).totalOutstanding;
    const collateralInfo = getZcLoanCollateral(loanExpanded);
    const collateralUncertainty = collateralInfo?.uncertainty ?? 0;

    const liquidationThreshold = getZcLoanLiquidationThreshold(loanExpanded);
    const liquidationPrice = calculateLiquidationPrice(totalOutstanding, collateralInfo?.amount, liquidationThreshold);

    const currentPrice = bsMath.div(
        collateralInfo && (collateralInfo.usdPrice ?? 0) + collateralUncertainty,
        loanExpanded.principalUsdPrice
    );
    const percentChange = calculatePercentChange(currentPrice, liquidationPrice);

    return { price: liquidationPrice, collateral, percentChange };
}

export function getLoanTotalDebtForLedger(ledgerAccount: LedgerExpanded | Ledger): LedgerDebt {
    const originalLedgers = summarizeLedgerAccount(ledgerAccount);

    return {
        total: originalLedgers.totalOutstanding,
        interestAccrued: originalLedgers.interestOutstanding
    };
}

export function calculatePrincipalInterestDeltaFromBorrowOrRepayEvent({ event }: { event: ParsedLoanEventExpanded }) {
    if (!event.action || !event.actionMetadata) return { principalDelta: 0, interestDelta: 0 };

    const metadata = event.actionMetadata;
    if (isEventActionMetadataType(metadata, DerivedLoanEventActionType.BorrowPrincipal)) {
        const { preBorrowPrincipalLedgerState: pre, postBorrowPrincipalLedgerState: post } = metadata.params;
        return {
            principalDelta: Math.abs(post.principalDue - pre.principalDue),
            interestDelta: Math.abs(post.interestOutstanding - pre.interestOutstanding)
        };
    }

    if (isEventActionMetadataType(metadata, DerivedLoanEventActionType.RepayPrincipal)) {
        const { preRepaymentPrincipalLedgerState: pre, postRepaymentPrincipalLedgerState: post } = metadata.params;
        return {
            principalDelta: Math.abs(post.principalRepaid - pre.principalRepaid),
            interestDelta: Math.abs(post.interestOutstanding - pre.interestOutstanding)
        };
    }

    if (
        isEventActionMetadataType(metadata, DerivedLoanEventActionType.PartialLiquidation) ||
        isEventActionMetadataType(metadata, DerivedLoanEventActionType.FullLiquidation)
    ) {
        const { repaymentEventMetadata } = metadata.params;
        const { preRepaymentPrincipalLedgerState: pre, postRepaymentPrincipalLedgerState: post } =
            repaymentEventMetadata;
        return {
            principalDelta: Math.abs(post.principalRepaid - pre.principalRepaid),
            interestDelta: Math.abs(post.interestOutstanding - pre.interestOutstanding)
        };
    }

    return { principalDelta: 0, interestDelta: 0 };
}

export function isDomainEventActionMetadataType<T extends LoanEventActionType>(
    metadata: ParsedLoanEventActionMetadata,
    type: T
): metadata is Extract<ParsedLoanEventActionMetadata, { type: T }> {
    return metadata.type === type;
}

export function isEventActionMetadataType<T extends DerivedLoanEventActionType>(
    metadata: DerivedLoanEventActionMetadata,
    type: T
): metadata is Extract<DerivedLoanEventActionMetadata, { type: T }>;

export function isEventActionMetadataType(
    metadata: DerivedLoanEventActionMetadata,
    type: DerivedLoanEventActionType
): boolean {
    return metadata.type === type;
}

export function isRepayEvent(action: LoanEventActionType | undefined) {
    return action === LoanEventActionType.RepayPrincipal;
}

export function isLiquidateEvent(action: LoanEventActionType | undefined) {
    return action === LoanEventActionType.Liquidate;
}

export function isFullLiquidationEvent(event: ParsedLoanEventExpanded) {
    return isEventActionMetadataType(event.actionMetadata, DerivedLoanEventActionType.FullLiquidation);
}

export function useIsLoanLender(loanExpanded: AbfLoanExpanded | undefined, activeWallet: string | undefined) {
    const isBorrower = loanExpanded?.borrower === activeWallet;
    const directLenderMatch = loanExpanded?.lenders?.includes(activeWallet ?? "") ?? false;

    const shouldSkipStrategies =
        !loanExpanded || !activeWallet || isBorrower || directLenderMatch || (loanExpanded?.lenders.length ?? 0) === 0;

    const { data: strategies } = useLenderStrategies({ skip: shouldSkipStrategies });

    const strategyAddress = useMemo(() => strategies?.map((s) => s.strategy.address), [strategies]);

    if (!loanExpanded || !activeWallet) return false;
    if (directLenderMatch) return true;
    if (!strategyAddress) return false;

    return (loanExpanded.lenders ?? []).some((lender) => strategyAddress.includes(lender));
}

/**
 * Permissioned RWA token utilities
 */

/**
 * Check if a token is a permissioned RWA token
 */
export function isPermissionedRwaToken(token: TokenListMetadata | undefined): boolean {
    return token?.tokenStandard === TokenStandard.PermissionedRWA;
}

/**
 * Check if any of the provided tokens are permissioned RWA tokens
 */
export function getLoanStrategyDuration(loanExpanded: AbfLoanExpanded | undefined): StrategyDuration | undefined {
    const ledger = getZcLedger(loanExpanded);
    if (!ledger) return undefined;

    return {
        duration: ledger.duration,
        durationType: ledger.durationType
    };
}
